import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { operationsApi } from '../api/services/operationsApi';
import {
  mergeHistoryFilterCards,
  normalizeHistoryFilterMetadata,
} from '../services/historyFilterMetadata';

const EMPTY_STAT = Object.freeze({
  raw: '0',
  value: '0',
  formatted: '$0.00',
});

const EMPTY_METADATA = Object.freeze({
  cards: [],
  minDate: null,
  maxDate: null,
  dateRange: { min: null, max: null },
  typeValues: [],
  stats: {
    income: EMPTY_STAT,
    outcome: EMPTY_STAT,
  },
  isMore: false,
});

function normalizeSelectedCardIds(cardIds) {
  return Array.from(new Set((cardIds || [])
    .map((cardId) => String(cardId ?? '').trim())
    .filter(Boolean)));
}

function getRangeBoundary(dateRange, key, fallbackKey) {
  const value = dateRange?.[key] ?? dateRange?.[fallbackKey];
  const normalized = String(value ?? '').trim();

  return normalized || undefined;
}

export default function useHistoryFilterMetadata({
  enabled = true,
  dateRange,
  cardIds = [],
} = {}) {
  const requestIdRef = useRef(0);
  const cardCatalogRef = useRef([]);
  const [refreshRevision, setRefreshRevision] = useState(0);
  const [state, setState] = useState({
    ...EMPTY_METADATA,
    isLoaded: false,
    isLoading: false,
    isRefreshing: false,
    error: null,
  });
  const cardIdsKey = normalizeSelectedCardIds(cardIds).join(',');
  const minDate = getRangeBoundary(dateRange, 'start', 'min');
  const maxDate = getRangeBoundary(dateRange, 'end', 'max');
  const requestParams = useMemo(() => ({
    cardIds: cardIdsKey ? cardIdsKey.split(',') : [],
    minDate,
    maxDate,
  }), [cardIdsKey, maxDate, minDate]);

  const refresh = useCallback(() => {
    setRefreshRevision((revision) => revision + 1);
  }, []);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;

    requestIdRef.current = requestId;

    if (!enabled) {
      setState((current) => ({
        ...current,
        isLoading: false,
        isRefreshing: false,
      }));
      return undefined;
    }

    let isActive = true;

    setState((current) => ({
      ...current,
      isLoading: !current.isLoaded,
      isRefreshing: current.isLoaded,
      error: null,
    }));
    console.info('[History debug] operation filters request', requestParams);

    operationsApi.getFilters(requestParams)
      .then((response) => {
        if (!isActive || requestIdRef.current !== requestId) return;

        const metadata = normalizeHistoryFilterMetadata(response);
        const cards = mergeHistoryFilterCards(cardCatalogRef.current, metadata.cards);

        cardCatalogRef.current = cards;
        console.info('[History debug] operation filters', response);
        setState({
          ...metadata,
          cards,
          isLoaded: true,
          isLoading: false,
          isRefreshing: false,
          error: null,
        });
      })
      .catch((error) => {
        if (!isActive || requestIdRef.current !== requestId) return;

        console.error('[History debug] failed to load operation filters', error);
        setState((current) => ({
          ...current,
          isLoading: false,
          isRefreshing: false,
          error,
        }));
      });

    return () => {
      isActive = false;
    };
  }, [enabled, refreshRevision, requestParams]);

  return {
    ...state,
    refresh,
  };
}

export { EMPTY_METADATA as EMPTY_HISTORY_FILTER_METADATA };
